import {ServiceBase} from '../ServiceBase';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {ISpinnerService} from './spinner.service.interface';
import {action, computed, observable, reaction} from 'mobx';
import {Uri} from '../location/uri/uri';
import {Check} from '@sparkware/app-utils';
import { isInsideAutomation } from '../../../utils/automation-helpers';

export const SPINNER_DELAY_TIME = 1000;
export const SPINNER_AUTO_CLOSE_TIMEOUT = 10000;

export class SpinnerService extends ServiceBase implements ISpinnerService {

  constructor(services: IServiceFactoryExtended) {
    super(services);
    this._previousUri = this.services.location.currentUri;

    this.services.splash.onSplashRemoved(() => {
      //Immediate after the splash is removed we start the spinner because at the time the splash is removed the games map is not yet initialized so all the games grids/swipers are empty
      this._internalStartSpinner();
    });

    this.services.location.onBeforeChangingLocation(this._onBeforeChangingLocationHandler.bind(this));
  }

  public shouldShowSpinner(): boolean {
    if (this._isB2CRoute) {
        return true;
    }

    return this.services.configuration.servicesConfigs.Spinner.enabled;
  }

  private _reactionOnIsFilteredGamesMapInitializedWasCanceled = false;
  private _isFilteredGamesMapInitializedReactionDisposer = null;
  private _hideSpinnerTimeout = null;
  private _cancelTheReactionOnIsFilteredGamesMapInitialized() {
    if(this._isFilteredGamesMapInitializedReactionDisposer) {
      this._isFilteredGamesMapInitializedReactionDisposer();
      this._isFilteredGamesMapInitializedReactionDisposer = null;
    }

    this._reactionOnIsFilteredGamesMapInitializedWasCanceled = true;
  }

  private _previousUri: Uri = null;

  @computed
  get isSpinning(): boolean {
    return !Check.isNullOrUndefined(this._startTime);
  }

  @observable.ref
  private _startTime: number = null;

  @action.bound
  private _setSpinningStartTime(value: number): void {
    this._startTime = value;
  }

  private _isB2CRoute: boolean = false;
  /**
   * This function shows/hide the spinner when the route is changed.
   * @param {Uri} newUri
   * @private
   */
  private _onBeforeChangingLocationHandler(newUri: Uri): void {
    this._isB2CRoute = !this.services.routing.isOrbitRoute(newUri.pathName);
    // this will prevent the spinner to show up in situation like applying filters. Basically we don't show the spinner when navigation happens inside the same page using a fragment or query params
    if(newUri.pathName.equals(this._previousUri && this._previousUri.pathName)) {
      this._previousUri = newUri;
    } else {
      //the user is going to navigate to another page so we show the spinner
      this.startSpinner();
      //and we initiate a reaction on currentRoute. So when the route is actually changed then we need to stop the spinner and discard this reaction.
      if (!this._isB2CRoute) { // for B2C pages we handle stopping the spinner from CmsPageComponent
        reaction(() => this.services.routing.currentRoute,
        (route, thisReaction) => {
          this.stopSpinner();
          thisReaction.dispose(); //this reaction disposes itself
        });
      }
    }
  }


  startSpinner(): void {
    this._cancelSpinnerStopIntervals();

    // if an explicit startSpinner was issued from outside (another component/service)
    // then we don't want that the reaction on isFilteredGamesMapInitialized to stop the spinner prematurely so we cancel that reaction
    this._cancelTheReactionOnIsFilteredGamesMapInitialized();
    this._startSpinnerAutoStop();
    this._internalStartSpinner();
  }

  private _internalStartSpinner() {
    this._setSpinningStartTime(this.services.time.performanceNow());
  }

  @action.bound
  stopSpinner() {
    if(!this.isSpinning) {
      return;
    }

    this._cancelSpinnerStopIntervals();
    // we delay the spinner for 1 second because sometime it is to fast and the user sees a flicker. but if already wait it should take total of 1 second.
    let timeoutForHideSpinner = SPINNER_DELAY_TIME - this.services.splash.computeSplashTimeInSeconds(this._startTime) * 1000;
    if(timeoutForHideSpinner < 0) {
      timeoutForHideSpinner = 0;
    }

    this._hideSpinnerTimeout = setTimeout(() => this._hideSpinner(), timeoutForHideSpinner);
    //we send the analytics now and not waiting for 1 second artificial delay introduce above.
    //we want to measure the real spinner time without this artificial delay.
    this._sendAnalytics(this._previousUri, this.services.location.currentUri);
    this._previousUri = this.services.location.currentUri;
  }

  private _enablePrerender() {
    const timeoutValue = isInsideAutomation() ? 0 : 5000;
    //We want to give 5 more sec of delay before pre render take the snapshot to make sure all b2c banner loaded
    setTimeout(() => {
      this.services.window.setGlobalProperty('prerenderReady', true);
    }, timeoutValue);
  }

  private _hideSpinner() {
    this._setSpinningStartTime(null);
    this._enablePrerender();
    this.services.webVitals?.siteFinishedLoading && (this.services.location.hasNavigationHistory===false) && this.services.webVitals.siteFinishedLoading();
  }

  private _sendAnalytics(sourceUri: Uri, targetUri: Uri): void {

    let spinnerTime = this.services.splash.computeSplashTimeInSeconds(this._startTime);
    let action = 'splash-time-splash_inner';

    let sourceRoute = (sourceUri && sourceUri.pathName.toString()) || '';
    let targetRoute = (targetUri && targetUri.pathName.toString()) || '';

    let label = sourceRoute + ' > ' + targetRoute;
    this.services.logger.info('Spinner time:' + spinnerTime);
    this.services.analytics.sendTechnicalEvent(action, label, spinnerTime);

    if(this.services.location?.hasNavigationHistory) {
      this.services.webVitals?.leavingPage && this.services.webVitals.leavingPage(sourceRoute);
    }
  }

  private _spinnerAutoStopTimer = null;
  private _startSpinnerAutoStop() {
    this._cancelSpinnerAutoStop();
    this._spinnerAutoStopTimer = setTimeout(() => {
      this._hideSpinner();
      this.services.logger.error(`Spinner was automatically closed after 10 seconds! Somebody didn't call stopSpinner!`);
    }, SPINNER_AUTO_CLOSE_TIMEOUT);
  }

  private _cancelSpinnerAutoStop() {
    if(this._spinnerAutoStopTimer) {
      clearTimeout(this._spinnerAutoStopTimer);
      this._spinnerAutoStopTimer = null;
    }
  }

  private _cancelSpinnerStopIntervals() {
    if(this._spinnerAutoStopTimer) {
      clearTimeout(this._spinnerAutoStopTimer);
      this._spinnerAutoStopTimer = null;
    }

    if(this._hideSpinnerTimeout){
      clearTimeout(this._hideSpinnerTimeout);
      this._hideSpinnerTimeout = null;
    }
  }
}
